export const PERMISSION_MODULES = {
  DASHBOARD: "Dashboard",
  MANAGE_USERS: "Manage Users",
  MANAGE_AGENTS: "Manage Agents",
  MANAGE_SUBSCRIPTION_PLANS: "Manage Subscription Plans",
  MANAGE_PROMOTIONAL_CODE: "Manage Promotional Code",
  MANAGE_CATEGORIES: "Manage Categories",
  MANAGE_PROPERTIES: "Manage Properties",
  PROMOTIONAL_TUTORIALS: "Promotional Tutorials",
  MANAGE_PAYMENTS: "Manage Payments",
  REFUND_REQUESTS: "Refund Requests",
  MANAGE_NOTIFICATIONS: "Manage Notifications",
  ANALYTICS: "Analytics",
  SETTINGS: "Settings",
  FAQS: "Faqs",
  MANAGE_AREAS: "Manage Areas",
  MANAGE_SUB_ADMIN: "Manage Sub-Admins",
} as const;

export type PermissionModuleName =
  (typeof PERMISSION_MODULES)[keyof typeof PERMISSION_MODULES];

export const PERMISSION_TO_ROUTE: Record<PermissionModuleName, string> = {
  [PERMISSION_MODULES.DASHBOARD]: "/dashboard",
  [PERMISSION_MODULES.MANAGE_USERS]: "/manageusers",
  [PERMISSION_MODULES.MANAGE_AGENTS]: "/manage-agents",
  [PERMISSION_MODULES.MANAGE_SUB_ADMIN]: "/manage-subAdmin",
  [PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS]: "/manage-subscription",
  [PERMISSION_MODULES.MANAGE_PROMOTIONAL_CODE]: "/manage-discount",
  [PERMISSION_MODULES.MANAGE_CATEGORIES]: "/manage-categories",
  [PERMISSION_MODULES.MANAGE_PROPERTIES]: "/manage-properties",
  [PERMISSION_MODULES.MANAGE_AREAS]: "/manage-areas",
  [PERMISSION_MODULES.PROMOTIONAL_TUTORIALS]: "/manage-tutorials",
  [PERMISSION_MODULES.MANAGE_PAYMENTS]: "/manage-payments",
  [PERMISSION_MODULES.REFUND_REQUESTS]: "/refund-requests",
  [PERMISSION_MODULES.MANAGE_NOTIFICATIONS]: "/manage-notifications",
  [PERMISSION_MODULES.ANALYTICS]: "/analytics",
  [PERMISSION_MODULES.SETTINGS]: "/cms",
  [PERMISSION_MODULES.FAQS]: "/faq",
};

export function getRouteForPermission(name?: string): string | undefined {
  if (!name) {
    return undefined;
  }
  return PERMISSION_TO_ROUTE[name as PermissionModuleName];
}

export type PagePermissionFlags = {
  isAdd: boolean;
  isDelete: boolean;
  isView: boolean;
};

export function getPagePermissionFlags(
  permissions: Array<{
    name?: string;
    isAdd?: boolean;
    isDelete?: boolean;
    isView?: boolean;
  }> | undefined,
  moduleName: PermissionModuleName,
  isAdmin = false,
): PagePermissionFlags {
  if (isAdmin) {
    return { isAdd: true, isDelete: true, isView: true };
  }
  if (!permissions?.length) {
    return { isAdd: false, isDelete: false, isView: false };
  }
  const match = permissions.find((item) => item?.name === moduleName);
  if (!match) {
    return { isAdd: false, isDelete: false, isView: false };
  }
  return {
    isAdd: !!match.isAdd,
    isDelete: !!match.isDelete,
    isView: !!match.isView,
  };
}

export function canShowTableActions(flags?: PagePermissionFlags | null) {
  return !!(flags?.isView || flags?.isAdd || flags?.isDelete);
}

export const SUBADMIN_ASSIGNABLE_PERMISSIONS: {
  name: PermissionModuleName;
  viewOnly?: boolean;
}[] = [
  { name: PERMISSION_MODULES.DASHBOARD },
  { name: PERMISSION_MODULES.MANAGE_USERS },
  { name: PERMISSION_MODULES.MANAGE_AGENTS },
  { name: PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS },
  { name: PERMISSION_MODULES.MANAGE_PROMOTIONAL_CODE },
  { name: PERMISSION_MODULES.MANAGE_CATEGORIES },
  { name: PERMISSION_MODULES.MANAGE_PROPERTIES },
  { name: PERMISSION_MODULES.MANAGE_AREAS },
  { name: PERMISSION_MODULES.PROMOTIONAL_TUTORIALS },
  { name: PERMISSION_MODULES.MANAGE_PAYMENTS, viewOnly: true },
  { name: PERMISSION_MODULES.REFUND_REQUESTS, viewOnly: true },
  { name: PERMISSION_MODULES.MANAGE_NOTIFICATIONS },
  { name: PERMISSION_MODULES.ANALYTICS, viewOnly: true },
  { name: PERMISSION_MODULES.SETTINGS, viewOnly: true },
  { name: PERMISSION_MODULES.FAQS, viewOnly: true },
  { name: PERMISSION_MODULES.MANAGE_SUB_ADMIN },
];
